import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserStore } from '@/store/userStore';
import { buildDraftKey, useFormDraftStore } from '@/store/formDraftStore';

/**
 * Brouillon persisté pour un formulaire à base de useState.
 *
 * - Au mount : si un brouillon non périmé existe pour (formId, salle, user),
 *   il est fusionné dans le state du formulaire (`hasRestoredDraft` passe à
 *   true — utile pour neutraliser les effets qui réécrivent des champs après
 *   coup, ex. hydratation des settings).
 * - À chaque frappe : sauvegarde debouncée (~400 ms), plus un flush immédiat
 *   quand l'app part en background (`visibilitychange → hidden`) — c'est là
 *   qu'Android peut tuer le process sans préavis.
 * - `clearDraft()` : à appeler sur succès du submit et sur annulation
 *   explicite.
 * - `applyDraft()` : ré-applique le brouillon courant à la demande (ex. à la
 *   réouverture d'un dialog dont le onOpenChange blanchit les champs).
 * - `hasRestoredDraftRef` : version synchrone de `hasRestoredDraft`, pour les
 *   gardes dans des callbacks ASYNC où la closure d'état est périmée (ex. un
 *   fetch de profil qui se termine après la restauration).
 */
export function useStateDraft<T extends Record<string, unknown>>(
  formId: string,
  formData: T,
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  options?: {
    /** Un state « vide » n'est jamais sauvegardé (et un brouillon vide jamais restauré). */
    isEmpty?: (data: T) => boolean;
    enabled?: boolean;
    /** Clés retirées du brouillon au save ET au restore (fichiers, URLs blob, champs lecture seule…). */
    exclude?: (keyof T & string)[];
    /**
     * Transformation du brouillon brut avant fusion (ex. re-hydrater des
     * dates : le JSON de localStorage a transformé Date→string, mais un
     * brouillon encore en mémoire peut contenir des Date vivantes — écrire
     * `new Date(v)` qui accepte les deux).
     */
    revive?: (data: Record<string, unknown>) => Partial<T>;
  }
) {
  const { isEmpty, enabled = true, exclude, revive } = options ?? {};
  const gymId = useUserStore((s) => s.gymId);
  const userId = useUserStore((s) => s.user?.id ?? null);
  const key = buildDraftKey(formId, gymId, userId);

  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const hasRestoredDraftRef = useRef(false);
  const restoredRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef({ formData, isEmpty, enabled, key, exclude, revive });
  latestRef.current = { formData, isEmpty, enabled, key, exclude, revive };

  const stripExcluded = useCallback((data: Record<string, unknown>) => {
    const ex = latestRef.current.exclude;
    if (!ex || ex.length === 0) return data;
    const next = { ...data };
    for (const k of ex) delete next[k];
    return next;
  }, []);

  /** Lit le brouillon courant, retiré des exclus et revivé — ou null. */
  const readDraft = useCallback((): Partial<T> | null => {
    const draft = useFormDraftStore.getState().getDraft(latestRef.current.key);
    const raw = draft?.data as Record<string, unknown> | undefined;
    if (!raw || typeof raw !== 'object') return null;
    const stripped = stripExcluded(raw);
    const rev = latestRef.current.revive;
    return (rev ? rev(stripped) : stripped) as Partial<T>;
  }, [stripExcluded]);

  /**
   * Fusionne le brouillon courant dans le state (à la demande). Retourne true
   * si quelque chose a été appliqué.
   */
  const applyDraft = useCallback((): boolean => {
    const data = readDraft();
    if (!data) return false;
    const { formData: current, isEmpty: empty } = latestRef.current;
    if (empty && empty({ ...current, ...data })) return false;
    setFormData((prev) => ({ ...prev, ...data }));
    hasRestoredDraftRef.current = true;
    setHasRestoredDraft(true);
    return true;
  }, [readDraft, setFormData]);

  // Restauration — une seule fois par montage du formulaire.
  useEffect(() => {
    if (!enabled || restoredRef.current) return;
    restoredRef.current = true;
    applyDraft();
    // key volontairement hors deps : un changement de salle/user en cours de
    // saisie ne doit pas re-restaurer par-dessus la frappe.
  }, [enabled, applyDraft]);

  // Sauvegarde debouncée.
  useEffect(() => {
    if (!enabled) return;
    if (!restoredRef.current) return; // pas avant la tentative de restauration
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { formData: data, isEmpty: empty, key: k } = latestRef.current;
      if (empty && empty(data)) return;
      useFormDraftStore.getState().saveDraft(k, stripExcluded(data));
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData, enabled, stripExcluded]);

  // Flush immédiat quand l'app passe en background.
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return;
      const { formData: data, isEmpty: empty, key: k } = latestRef.current;
      if (empty && empty(data)) return;
      useFormDraftStore.getState().saveDraft(k, stripExcluded(data));
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, stripExcluded]);

  const clearDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    useFormDraftStore.getState().clearDraft(latestRef.current.key);
  }, []);

  return { hasRestoredDraft, hasRestoredDraftRef, clearDraft, applyDraft };
}
